import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Button, Alert, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BiSortUp, BiSortDown, BiTrash } from 'react-icons/bi'; // Import Bootstrap icons for sorting and deleting
import { FaPlus } from 'react-icons/fa';
import './Provider.scss';

const Provider = () => {
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [filters, setFilters] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: '' });
  const [filterBoxOpen, setFilterBoxOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [providersResponse, providerTypesResponse] = await Promise.all([
          axios.get('/api/data'),
          axios.get('/api/provider_types'),
        ]);
        setProviders(providersResponse.data);
        setProviderTypes(providerTypesResponse.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    applyFilterAndSort(providers, filters, sortConfig, searchTerm);
  }, [filters, providers, sortConfig, searchTerm]);

  const applyFilterAndSort = (providers, filters, sortConfig, searchTerm) => {
    let filteredProviders = providers;

    // Apply filter by provider type
    if (filters.length > 0) {
      filteredProviders = filteredProviders.filter(provider => filters.includes(provider.provider_type_name));
    }

    // Apply search filter
    if (searchTerm) {
      filteredProviders = filteredProviders.filter(provider =>
        provider.provider_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredProviders.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredProviders(filteredProviders);
  };

  const handleFilterChange = e => {
    const { value, checked } = e.target;

    if (checked) {
      setFilters(prevFilters => [...prevFilters, value]);
    } else {
      setFilters(prevFilters => prevFilters.filter(filter => filter !== value));
    }
  };

  const requestSort = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const toggleFilterBox = () => {
    setFilterBoxOpen(!filterBoxOpen);
  };

  const clearFilters = () => {
    setFilters([]);
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const handleDelete = provider => {
    setSelectedProvider(provider);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/provider/${selectedProvider.provider_id}`);
      setSuccessMessage('Provider successfully deleted.');
      setProviders(providers.filter(provider => provider.provider_id !== selectedProvider.provider_id));
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting provider', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div className="provider-page">
      <div className="d-flex justify-content-between align-items-center">
        <h1>Provider Page</h1>
        <Dropdown>
          <Dropdown.Toggle variant="primary" id="dropdown-basic">
            Options
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item onClick={() => navigate('/provider/add')}>
              <FaPlus className="me-2" /> Add New Provider
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}
      <div className="filter-section">
        <button className="btn btn-primary" onClick={toggleFilterBox}>
          Filter
        </button>
        {filterBoxOpen && (
          <div className="card mt-3">
            <div className="card-body">
              <h5 className="card-title">Provider Types</h5>
              <div className="d-flex flex-wrap">
                {providerTypes.map(type => (
                  <label key={type.provider_type_id} className="filter-label">
                    <input
                      type="checkbox"
                      value={type.provider_type_name}
                      onChange={handleFilterChange}
                      checked={filters.includes(type.provider_type_name)}
                    />
                    {type.provider_type_name}
                  </label>
                ))}
              </div>
              <button className="btn btn-secondary clear-filters" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="search-section">
        <input
          type="text"
          className="form-control"
          placeholder="Search by provider name"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
      <div className="table-container">
        <table className="table table-striped">
          <thead>
            <tr>
              <th onClick={() => requestSort('provider_name')}>
                Provider Name{' '}
                {sortConfig.key === 'provider_name' && (
                  <span>{sortConfig.direction === 'ascending' ? <BiSortUp /> : <BiSortDown />}</span>
                )}
              </th>
              <th onClick={() => requestSort('provider_type_name')}>
                Provider Type{' '}
                {sortConfig.key === 'provider_type_name' && (
                  <span>{sortConfig.direction === 'ascending' ? <BiSortUp /> : <BiSortDown />}</span>
                )}
              </th>
              <th onClick={() => requestSort('provider_scac')}>
                Provider SCAC{' '}
                {sortConfig.key === 'provider_scac' && (
                  <span>{sortConfig.direction === 'ascending' ? <BiSortUp /> : <BiSortDown />}</span>
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProviders.length > 0 ? (
              filteredProviders.map(provider => (
                <tr key={provider.provider_id}>
                  <td>{provider.provider_name}</td>
                  <td>{provider.provider_type_name}</td>
                  <td>{provider.provider_scac}</td>
                  <td>
                    <OverlayTrigger
                      overlay={<Tooltip id={`tooltip-delete-${provider.provider_id}`}>Delete Provider</Tooltip>}
                    >
                      <Button variant="danger" className="action-button" onClick={() => handleDelete(provider)}>
                        <BiTrash />
                      </Button>
                    </OverlayTrigger>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="no-results">No results for the specified filter</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Provider</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Deleting this record is permanent and cannot be undone.</p>
          {selectedProvider && (
            <table className="table">
              <thead>
                <tr>
                  <th>Provider Name</th>
                  <th>Provider SCAC</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{selectedProvider.provider_name}</td>
                  <td>{selectedProvider.provider_scac}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete}>Delete Provider</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Provider;
